export type PaginationResult<T> = Readonly<{
  items: T[];
  currentPage: number;
  totalPages: number;
}>;

export const RESOURCE_PAGE_SIZE = 40;

function normalizePageSize(pageSize: number): number {
  if (!Number.isFinite(pageSize)) return RESOURCE_PAGE_SIZE;
  return Math.max(1, Math.floor(pageSize));
}

function normalizeTotalPages(totalItems: number, pageSize: number): number {
  if (totalItems <= 0) return 1;
  return Math.max(1, Math.ceil(totalItems / pageSize));
}

function normalizePage(page: number, totalPages: number): number {
  if (!Number.isFinite(page)) return 1;
  return Math.min(totalPages, Math.max(1, Math.floor(page)));
}

export function paginateItems<T>(items: T[], page: number, pageSize: number): PaginationResult<T> {
  const normalizedItems = Array.isArray(items) ? items : [];
  const normalizedPageSize = normalizePageSize(pageSize);
  const totalPages = normalizeTotalPages(normalizedItems.length, normalizedPageSize);
  const currentPage = normalizePage(page, totalPages);

  const start = (currentPage - 1) * normalizedPageSize;
  const end = start + normalizedPageSize;

  return {
    items: normalizedItems.slice(start, end),
    currentPage,
    totalPages,
  };
}

export function buildPaginationNumbers(currentPage: number, totalPages: number, maxVisible = 7): number[] {
  const normalizedTotalPages = Math.max(1, Math.floor(totalPages || 1));
  const normalizedCurrentPage = Math.min(normalizedTotalPages, Math.max(1, Math.floor(currentPage || 1)));
  const normalizedMaxVisible = Math.max(3, Math.floor(maxVisible || 7));

  if (normalizedTotalPages <= normalizedMaxVisible) {
    return Array.from({ length: normalizedTotalPages }, (_, index) => index + 1);
  }

  const half = Math.floor(normalizedMaxVisible / 2);
  let start = normalizedCurrentPage - half;
  let end = normalizedCurrentPage + half;

  if (normalizedMaxVisible % 2 === 0) {
    end -= 1;
  }

  if (start < 1) {
    end += 1 - start;
    start = 1;
  }

  if (end > normalizedTotalPages) {
    start -= end - normalizedTotalPages;
    end = normalizedTotalPages;
  }

  start = Math.max(1, start);

  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}
